import {closeDialog, openDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  DmsDataGrid,
  PriceListExternalFilters,
  PriceListRowData,
  WAREHOUSE_GRID_CODES,
  catchUnhandledDataGridActions,
  logError,
} from '@dms/shared';
import {ArticleCreate, PriceListDetail} from '@dms/shared-aftersales';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {
  ActionCallback,
  FormToDataGridConnectorProps,
  QueryFilterObject,
} from 'features/eag-grid';

// filterComponent is rendered as a JSX element type by both grid engines, so it must keep a
// stable identity — an inline arrow would remount the whole stateful filter form on every
// PriceListInner render (see PriceListFiltersWithDefaults in MaterialPriceListTab).
function PriceListFiltersWithAuthorizationProfile(props: FormToDataGridConnectorProps) {
  return <PriceListExternalFilters {...props} hasAuthorizationProfileFilter />;
}

const CREATE_ARTICLE_DIALOG = 'createArticleDialog';

const PRICE_LIST_EXTERNAL_FILTER_ID = 'price-list-external-filter';

const PREDEFINED_COLUMNS = [
  'name',
  'catalogNumber',
  'orderingNumber',
  'manufacturerId',
  'supplierId',
  'supplierAccount',
  'supplierOrderType',
  'branchId',
  'authorizationProfileId',
  'purchasePrice',
  'recommendedPrice',
  'supplierDiscountGroup',
  'supplierOrderingNumber',
  'supplierManufacturerName',
  'supplierManufacturerCode',
  'standardOrderQuantity',
  'localStock',
  'totalStock',
] as const;

export function PriceListInner() {
  const navigate = useNavigate();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
  }) => {
    const priceListId = isArray(rowId) ? head(rowId) : rowId;

    const priceListRowData = {
      name: {value: getColumnData('name')?.value},
      catalogNumber: {value: getColumnData('catalogNumber')?.value},
      supplierOrderingNumber: {value: getColumnData('supplierOrderingNumber')?.value},
      manufacturerId: {value: getColumnData('manufacturerId')?.value},
      supplierId: {value: getColumnData('supplierId')?.value},
      purchasePrice: {value: getColumnData('purchasePrice')?.value},
      recommendedPrice: {value: getColumnData('recommendedPrice')?.value},
      supplierDiscountGroup: getColumnData('supplierDiscountGroup')?.value,
      supplierManufacturerName: getColumnData('supplierManufacturerName')?.value,
      supplierManufacturerCode: getColumnData('supplierManufacturerCode')?.value,
      standardOrderQuantity: getColumnData('standardOrderQuantity')?.value,
      localStock: getColumnData('localStock')?.value,
      totalStock: getColumnData('totalStock')?.value,
      supplierAccountCode: getColumnData<string>('supplierAccount', {isOptional: true})?.value,
      supplierOrderTypeCode: getColumnData<string>('supplierOrderType', {isOptional: true})?.value,
      branchId: getColumnData<string>('branchId', {isOptional: true})?.value,
      authorizationProfileId: getColumnData<string>('authorizationProfileId', {isOptional: true})
        ?.value,
    } as PriceListRowData;

    if (isNil(priceListId)) {
      return logError('priceListId was not defined');
    }

    match(actionKey)
      .with('redirectDetail', () => {
        openDialog(
          <PriceListDetail
            id={priceListId}
            priceListRowData={priceListRowData}
            data-testid={testIds.warehouse.priceList('dialogs.detail')}
          />,
          {
            id: 'detailDialog',
            title: priceListRowData?.name?.value,
            size: 'large',
            withAdditionalFooter: true,
          }
        );
      })
      .with('createArticle', () => {
        openDialog(
          <ArticleCreate
            defaultValues={{
              catalogNumber: priceListRowData?.catalogNumber?.value,
              manufacturerId: priceListRowData?.manufacturerId?.value?.id,
              name: priceListRowData?.name?.value,
            }}
            onArticleCreate={(createdArticleData) => {
              if (createdArticleData.submittedButton === 'CREATE_AND_EDIT') {
                return navigate(
                  composePath(warehouseRoutes.articleDetailOverview, {
                    params: {
                      warehouseId: createdArticleData.formValues.warehouseId,
                      id: createdArticleData.articleId,
                    },
                  })
                );
              }

              closeDialog(CREATE_ARTICLE_DIALOG);
            }}
            onClose={() => closeDialog(CREATE_ARTICLE_DIALOG)}
            hasCreateAndEditButton
            data-testid={testIds.warehouse.priceList('dialogs.createArticle')}
          />,
          {
            id: CREATE_ARTICLE_DIALOG,
            title: i18n.t('entity.warehouse.labels.createNewArticle'),
            withAdditionalFooter: true,
            scrollBehavior: 'outside',
          }
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  // branchId stays branch-header driven; authorizationProfileId flows from the external filter's
  // single-choice select, so it must survive the merge.
  const queryModifier = (filter: QueryFilterObject) => mergeAll([filter, {branchId: undefined}]);

  return (
    <FullscreenDataGridWrapper
      externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
      data-testid={testIds.warehouse.priceList('externalFilterWithFolderTree')}
    >
      <DmsDataGrid
        // DG must be re-rendered on folder change to update the query modifier
        gridCode={WAREHOUSE_GRID_CODES.supplierPriceList}
        externalFilterId={PRICE_LIST_EXTERNAL_FILTER_ID}
        actionCallback={actionCallback}
        predefinedColumns={PREDEFINED_COLUMNS}
        _useAsLastResort_definitionBehaviorOverrides={{filterMode: 'NATIVE'}}
        filterComponent={PriceListFiltersWithAuthorizationProfile}
        data-testid={WAREHOUSE_GRID_CODES.supplierPriceList}
        emptyState={{
          headline: i18n.t('page.warehouseDetail.labels.emptyAssortment'),
        }}
        queryModifier={queryModifier}
      />
    </FullscreenDataGridWrapper>
  );
}
